// Phase 16 — closed-list autocomplete combobox.
//
// Replaces plain <select> at the four Section / Jurisdiction picker
// sites where the option count had grown past comfortable scrolling
// (Library 46 sections, Jurisdictions can grow indefinitely). Users
// type to filter live; the dropdown shows the matching subset; saves
// only resolve to a known option (closed list, no free text).
//
// UX contract:
//   - input shows the resolved label for `value`
//   - typing filters options by substring (case- and accent-insensitive)
//   - ArrowDown opens the dropdown; ArrowUp/ArrowDown move highlight;
//     Enter selects; Escape closes without changing
//   - on blur, if the typed text exactly matches one option label
//     (case-insensitive), that option is committed; otherwise the
//     input reverts to the previously-selected value's label
//   - clicking outside closes + reverts
//   - clearable via a small × button when a value is set
//
// `options` shape: [{ value, label, sub? }]
//   value : the underlying storage value (string; callers convert
//           ints with String() and parse on the way back)
//   label : the primary display string (active-language label)
//   sub   : optional secondary line (e.g. city for a court)
//
// Phase 16 correction #6 — in-flow positioning, NO portal.
//
// Earlier revisions portalled the dropdown to document.body and
// positioned it with position:fixed + getBoundingClientRect coords.
// That broke in this app because the app root applies a UI zoom via
// --ui-zoom on body/#root (height: calc(100dvh / var(--ui-zoom,1))).
// A body-child with position:fixed renders inside that zoomed
// context, so the unscaled rect coords (top:292.6 left:297.3) land
// off-target — width matches the input, but x/y are wrong by a factor
// of the zoom, error scaling with distance from the origin.
//
// Fix: render the menu as a position:absolute sibling of the input,
// inside the component's relative-positioned wrapper. The menu shares
// the field's coordinate space and any zoom applies equally to both,
// so the anchor stays correct without any rect math, scroll listener,
// or ResizeObserver. Modal overflow:hidden (.MB has overflow-y:auto,
// not hidden — long lists scroll inside the modal body, which is the
// correct behaviour for a closed-list combobox).
//
// Flip-up: on open, measure the room above and below the field inside
// its clip box, once. BRIEF 114 §2 — the rule is "below unless below
// cannot host a usable menu", not "whichever side is larger": a
// dropdown belongs under its field, and the menu's height is the
// winning side's room capped at MENU_MAX_HEIGHT, with internal scroll
// doing the rest. No live scroll/resize listeners needed.

import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { ChevronDown, X as XIcon } from "lucide-react";
import { RAD, FS, Z } from "../lib/tokens";
import { NO_AUTOFILL } from "../lib/noAutofill"; // 213B §8 — no sign-ins, no contacts, in a picker
import { useTranslation } from "../i18n";

// Diacritic-insensitive substring match. "réf" matches "REFERE" and
// "Référé"; "ant" matches "Antwerpen". Cheap to compute per keystroke
// at the option counts we see in practice (<= ~200).
function normalize(s: any) {
  return String(s || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

const MENU_MAX_HEIGHT = 300;
const MENU_GAP = 4;
const CLIP_MARGIN = 8;
/* BRIEF 114 §2 — WAS `MIN_MENU_HEIGHT`, AND THE NAME WAS THE BUG.
   It read as a floor under the menu's height and was applied as one
   (`Math.max(MIN_MENU_HEIGHT, Math.min(MENU_MAX_HEIGHT, space))`), so in a clip
   box with less room than this it BEAT the cap that exists to keep the menu
   inside that box. 113 §4 measured what that costs: the menu overflowed a 140px
   dialog body by 34px, the overflowing rows were painted but sat outside the
   scroll container, and a pointer aimed at one reached the dialog HEADER — no
   toast, no error, no write. It is now what it should always have been: the
   room BELOW the field that counts as usable, i.e. the threshold that decides a
   flip — never a licence to overflow. Renamed rather than re-commented, because
   a constant whose name says "minimum" will be applied as a minimum by the next
   reader. */
const PREFERRED_MIN_HEIGHT = 120;

// Phase 16 correction #6 follow-up — viewport isn't the right clip
// reference inside modals. The modal body (.MB) scrolls with
// overflow-y:auto, so the dropdown's effective visible area is bounded
// by that scroll container, not by window.innerHeight. With a 720px
// modal centered in an 800px viewport and a 46-row Section list, the
// viewport-based math gave the menu 300px regardless — and the menu
// then ran past the modal's top or bottom edge and the clipped rows
// were unreachable because they were outside the .MB scroll container,
// not outside the menu's own overflow.
//
// Walk up from the field, intersect every ancestor's getBoundingClientRect
// when it has overflow auto / scroll / hidden on Y. The resulting
// {top, bottom} is the union of all clip boxes the dropdown must fit
// inside. Falls back to viewport bounds when no clip ancestor exists.
function findClipRect(el: HTMLElement | null) {
  let top = 0;
  let bottom = (typeof window !== "undefined" && window.innerHeight) || 800;
  let p = el && el.parentElement;
  while (p && p !== document.body && p !== document.documentElement) {
    const cs = getComputedStyle(p);
    const oy = cs.overflowY;
    if (oy === "auto" || oy === "scroll" || oy === "hidden") {
      const r = p.getBoundingClientRect();
      if (r.top > top) top = r.top;
      if (r.bottom < bottom) bottom = r.bottom;
    }
    p = p.parentElement;
  }
  return { top, bottom };
}

type Option = { value: any; label: string; sub?: string };
type ComboboxProps = {
  value?: any;
  onChange?: (value: any) => void;
  options?: Option[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  style?: CSSProperties;
  id?: string;
};

export default function Combobox({
  value,
  onChange,
  options,
  placeholder = "",
  disabled = false,
  allowClear = true,
  /* BRIEF 108 §8b — `"fi"` was brief 80 §3's dead coordinate: no `.fi` rule
     exists in App.css (its comments at :1113 and :1978 carry the lesson), so
     the closed trigger painted NO background and NO border, unlike every
     `.field-input` sibling around it. All 4 mounts (CaseModal jurisdiction,
     StageModal jurisdiction, CollectiveCaseView jurisdiction, LibraryView
     section) pass no className and sit in `.field` rows among `.field-input`
     controls — parity is right at the seam, not per caller. The div keeps
     `min-height` from the class; the `height` pin applies to input./select.
     only, so the flex trigger still fits its chevron and clear button. */
  className = "field-input",
  style,
  id,
}: ComboboxProps) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  // Phase 16 #6 follow-up — track both flip direction and the
  // available-space cap together. Re-measured once per open. We
  // deliberately do NOT re-evaluate on scroll/resize: once the menu is
  // open the user is interacting with it, and changing direction or
  // height under their cursor is more disruptive than living with a
  // slightly suboptimal placement.
  const [placement, setPlacement] = useState({
    flipUp: false,
    maxH: MENU_MAX_HEIGHT,
  });
  /* 114 §2 — MENU_MAX_HEIGHT as the initial value is safe and stays: the
     layout effect below runs before paint on the same commit that opens the
     menu, so this figure is never the one a user sees. */

  const selected = useMemo(
    () => (options || []).find((o) => String(o.value) === String(value ?? "")),
    [options, value],
  );
  const selectedLabel = selected?.label ?? "";

  // Empty query → show all options. Diacritic + case insensitive.
  const filtered = useMemo(() => {
    if (!options) return [];
    const q = normalize(query);
    if (!q) return options;
    return options.filter(
      (o) =>
        normalize(o.label).includes(q) ||
        normalize(o.sub || "").includes(q) ||
        normalize(o.value).includes(q),
    );
  }, [options, query]);

  // Reset highlight when the filter shifts or the menu (re)opens.
  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  // Placement decision happens once per open. Measure space ABOVE and
  // BELOW the field, bounded by every ancestor scroll/clip container,
  // prefer below, and cap max-height to the winning side's room (minus
  // a small margin). Without that cap a 46-row dropdown would run past
  // the modal body's clip box and the user couldn't reach the overrun
  // rows: they'd be outside the modal's overflow region, not outside
  // the menu's own overflow. 114 §2 — and the cap only became one when
  // the 120px floor stopped outranking it; see below.
  useLayoutEffect(() => {
    if (!open) {
      setPlacement({ flipUp: false, maxH: MENU_MAX_HEIGHT });
      return;
    }
    const el = fieldRef.current;
    if (!el) return;
    const fieldRect = el.getBoundingClientRect();
    const clip = findClipRect(el);
    const spaceBelow = clip.bottom - fieldRect.bottom - MENU_GAP - CLIP_MARGIN;
    const spaceAbove = fieldRect.top - clip.top - MENU_GAP - CLIP_MARGIN;
    /* BRIEF 114 §2 — THE MENU NEVER EXCEEDS THE SPACE IT CAN ACTUALLY RENDER IN.
       Two changes, both in these three lines:
       (a) BELOW IS PREFERRED, not merely "whichever side is larger". A dropdown
           belongs under its field; the old rule flipped a perfectly usable
           250px-below menu upward for the sake of 10px more above. It flips only
           when below cannot host a usable menu AND above does better — which
           keeps every flip the old rule got RIGHT (a field at the bottom of its
           clip box) and drops the ones it got gratuitously.
       (b) THE CAP IS THE WINNING SIDE'S ROOM, FULL STOP. `Math.max(120, …)` used
           to raise the height back above the room measured, which is how the
           menu came to overflow its own clip box (113 §4, measured: 34px past a
           140px dialog body, rows painted and unclickable). `overflow-y: auto`
           on the menu already makes a short menu fully reachable by scrolling
           INSIDE it — a scrollbar is a cost; an unclickable row is a defect.
       The degenerate case is stated rather than papered over: a clip box with
       almost no room on either side yields an almost-invisible menu. That is the
       truth of such a box — the old code's answer was a menu that looked right
       and could not be clicked. */
    const flipUp = spaceBelow < PREFERRED_MIN_HEIGHT && spaceAbove > spaceBelow;
    const space = flipUp ? spaceAbove : spaceBelow;
    const maxH = Math.max(0, Math.min(MENU_MAX_HEIGHT, space));
    setPlacement({ flipUp, maxH });
  }, [open]);

  // Outside-click handler. Click outside the wrapper closes the menu;
  // commit-or-revert any typed text on the way out.
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      commitOnBlur();
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, options]);

  const openMenu = () => {
    if (disabled) return;
    setQuery("");
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commit = (opt: Option | null) => {
    onChange?.(opt ? opt.value : "");
    setQuery("");
    setOpen(false);
  };

  // On blur (or outside click): commit if exact-label match, else
  // silently revert to the prior value.
  const commitOnBlur = () => {
    if (!open) return;
    if (!query) return;
    const nq = normalize(query);
    const exact = (options || []).find((o) => normalize(o.label) === nq);
    if (exact) onChange?.(exact.value);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) commit(opt);
      return;
    }
    if (e.key === "Tab") {
      commitOnBlur();
      setOpen(false);
      return;
    }
  };

  // Menu positioning is direction-aware. width:100% matches the field
  // horizontally (independent of UI zoom, since it's an in-flow child
  // of the relative wrapper). max-height is computed from the room
  // available inside the modal/scroll-container clip box at open time;
  // overflow-y:auto then lets the full list be reached via internal
  // scroll without ever extending past the clip box.
  const menuStyle: CSSProperties = {
    position: "absolute",
    left: 0,
    width: "100%",
    maxHeight: placement.maxH,
    overflowY: "auto",
    background: "var(--bc)",
    border: "1px solid var(--br)",
    borderRadius: RAD.r8,
    boxShadow: "0 6px 20px rgba(0,0,0,.6)",
    zIndex: Z.portal,
    fontSize: FS.s11,
    ...(placement.flipUp
      ? { bottom: `calc(100% + ${MENU_GAP}px)`, top: "auto" }
      : { top: `calc(100% + ${MENU_GAP}px)`, bottom: "auto" }),
  };

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", ...(style || {}) }}
      id={id}
    >
      <div
        ref={fieldRef}
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          cursor: disabled ? "not-allowed" : "text",
          paddingRight: 6,
          opacity: disabled ? 0.65 : 1,
        }}
        onClick={openMenu}
      >
        {open ? (
          <input
            ref={inputRef}
            type="text"
            /* 213B §8 — not a sign-in field (src/lib/noAutofill.ts); its own
               autoComplete="off" below is now the bundle's */
            {...NO_AUTOFILL}
            aria-autocomplete="list"
            className="no-contact-autofill"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={selectedLabel || placeholder}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              color: "var(--tp)",
              fontFamily: "inherit",
              fontSize: "inherit",
              padding: 0,
              minWidth: 0,
            }}
          />
        ) : (
          <span
            style={{
              flex: 1,
              color: selectedLabel ? "var(--tp)" : "var(--tm)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedLabel || placeholder}
          </span>
        )}
        {allowClear && selectedLabel && !disabled && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              commit(null);
            }}
            style={{
              background: "none",
              border: "none",
              color: "var(--tm)",
              cursor: "pointer",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
            }}
            title={t("common.clear", { fallback: "Clear" })}
          >
            <XIcon size={11} />
          </button>
        )}
        <ChevronDown size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
      </div>

      {open && (
        <div data-combobox-menu style={menuStyle}>
          {filtered.length === 0 ? (
            <div
              style={{
                padding: "6px 10px",
                color: "var(--tm)",
                fontStyle: "italic",
              }}
            >
              {t("common.no_matches", { fallback: "No matches." })}
            </div>
          ) : (
            filtered.map((o, i) => (
              <div
                key={String(o.value)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(o);
                }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: "5px 10px",
                  cursor: "pointer",
                  background: i === highlight ? "var(--bh)" : "transparent",
                  borderBottom: "1px solid rgba(255,255,255,.04)",
                }}
              >
                <div style={{ color: "var(--tp)" }}>{o.label}</div>
                {o.sub && (
                  <div style={{ color: "var(--tm)", fontSize: FS.s9_5 }}>
                    {o.sub}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
